import { isString } from 'lodash';
import { makeBenefitsApi, makeMissionsApi } from 'src/http/api/internal/internal-http';
import { CouponsRepository } from 'src/repositories/benefits/coupons.repository';
import { MissionsRepository } from 'src/repositories/missions/missions.repository';
import { missionsTypeItem } from 'src/types/missions';
import { toBase64 } from 'src/utils/utils';
import { create } from 'zustand';

type States = {
  isLoadingMissionsList: boolean;
  isLoadingMissionDetails: boolean;
  isLoadingCreateMission: boolean;
  isLoadingUpdateMission: boolean;
  missionsList: missionsTypeItem[];
  missionDetails: missionsTypeItem | null;
};

type Actions = {
  getMissionsList: () => Promise<void>;
  getMissionDetails: (id: string) => Promise<void>;
  resetMissionDetails: () => void;
  createMission: (mission: any) => Promise<void>;
  updateMission: (mission: any) => Promise<void>;
};

type Missions = Actions & States;

const initialValues = {
  isLoadingMissionsList: false,
  isLoadingMissionDetails: false,
  isLoadingCreateMission: false,
  isLoadingUpdateMission: false,
  missionsList: [],
  missionDetails: null,
};

const missionsRepository = new MissionsRepository(makeMissionsApi());
const couponsRepository = new CouponsRepository(makeBenefitsApi());

export const useMissionsStore = create<Missions>((set: any, get: any) => ({
  ...initialValues,

  getMissionsList: async () => {
    set({ isLoadingMissionsList: true });
    const response = await missionsRepository.getMissionsList();

    if (response.isLeft()) {
      set({ isLoadingMissionsList: false });
      throw new Error(response.value.message);
    } else {
      set({ missionsList: response.value, isLoadingMissionsList: false });
    }
  },

  getMissionDetails: async (id) => {
    set({ isLoadingMissionDetails: true });
    const response = await missionsRepository.getMissionDetails(id);

    if (response.isLeft()) {
      set({ isLoadingMissionDetails: false });
      throw new Error(response.value.message);
    } else {
      set({ missionDetails: response.value, isLoadingMissionDetails: false });
    }
  },

  resetMissionDetails: () => {
    set({ missionDetails: null });
  },

  createMission: async (mission) => {
    set({ isLoadingCreateMission: true });
    if (mission?.instantData?.imageResult && !isString(mission?.instantData?.imageResult)) {
      try {
        const imgBase64: any = await toBase64(mission?.instantData?.imageResult);
        const res: any = await couponsRepository.uploadFile({
          name: mission?.instantData?.imageResult.name,
          base64: imgBase64,
        });
        mission.instantData.imageResult = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }
    const response = await missionsRepository.createMission(mission);

    if (response.isLeft()) {
      set({ isLoadingCreateMission: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingCreateMission: false });
    }
  },

  updateMission: async (mission) => {
    set({ isLoadingUpdateMission: true });
    if (mission?.instantData?.imageResult && !isString(mission?.instantData?.imageResult)) {
      try {
        const imgBase64: any = await toBase64(mission?.instantData?.imageResult);
        const res: any = await couponsRepository.uploadFile({
          name: mission?.instantData?.imageResult.name,
          base64: imgBase64,
        });
        mission.instantData.imageResult = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }
    const response = await missionsRepository.updateMission(mission);

    if (response.isLeft()) {
      set({ isLoadingUpdateMission: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingUpdateMission: false });
    }
  },
}));
